import {DateTime} from 'luxon';
import {RRule, RRuleSet, rrulestr, RRuleStrOptions} from 'rrule';

import {isDefined, isInteger} from '../../libs/shared/helpers/src/typescript/typeHelpers';

export function toEpochMillisFromRRuleLibraryDate(jsDate: Date) {
  return DateTime.fromJSDate(jsDate).toUTC().setZone('local', {keepLocalTime: true}).valueOf();
}

/*
 * RRule parsing.
 */

interface ParseRecurrenceRuleOptions {
  timezone?: string | null;
  startsAt?: number;
}

export function parseRecurrenceRuleFromString(
  recurrenceString: string,
  {timezone, startsAt}: ParseRecurrenceRuleOptions = {},
) {
  const ruleOptions: Partial<RRuleStrOptions> = {
    // RRule library doesn't recognize short form timezone name, so convert UTC to its long form name.
    tzid: timezone === 'UTC' ? 'Etc/UTC' : timezone,
    dtstart: isInteger(startsAt) ? toRRuleLibraryDateFromEpochMillis(startsAt, timezone) : undefined,
  };

  // Generate all the instance start times within the current range.
  // RRule only supports UTC times so we force the timezone conversion
  // whenever providing inputs to or parsing outputs from the library.
  // More info: https://github.com/jakubroztocil/rrule#important-use-utc-dates
  const parsedRuleOrRuleSet = rrulestr(recurrenceString, ruleOptions);

  return normalizeRuleSet(parsedRuleOrRuleSet, ruleOptions);
}

function toRRuleLibraryDateFromEpochMillis(epochMillis: number, optionalTimezone: string | null | undefined) {
  return DateTime.fromMillis(epochMillis)
    .setZone(optionalTimezone || 'local')
    .setZone('utc', {keepLocalTime: true})
    .toJSDate();
}

// PB-22607 Temporary Patch: The rrule library has a bug with rrulestr when there is more than
// one rule parsed at once (for example an RDATE with a RRULE). In these cases, the dtstart that's
// passed into the options argument of `rrulestr` isn't carried into the recurrence rule itself.
// This causes the recurrence rule to be expanded onto the wrong start times. To address this, we
// explicitly build a new RRuleSet using the constructor when this could possibly happen.
function normalizeRuleSet(ruleOrRuleSet: RRule | RRuleSet, ruleOptions: Partial<RRuleStrOptions>) {
  if (!(ruleOrRuleSet instanceof RRuleSet)) {
    return ruleOrRuleSet;
  }

  const ruleset = ruleOrRuleSet;
  const recurrenceRuleRecurrences = ruleset.rrules();
  const exceptionDateRecurrences = ruleset.exdates();
  const recurrenceDateRecurrences = ruleset.rdates();

  const normalizedRuleSet = new RRuleSet();
  const {tzid = ruleOrRuleSet.tzid(), dtstart} = ruleOptions;

  if (isDefined(tzid)) {
    normalizedRuleSet.tzid(tzid);
  }

  if (isDefined(dtstart)) {
    normalizedRuleSet.dtstart(dtstart);
  }

  exceptionDateRecurrences.forEach((exdate) => normalizedRuleSet.exdate(exdate));

  // Note, we may be disregarding a parsed tzid at this point. Per the rrule spec, exdate and
  // rdate tzid should be the same as dtstart. Since we always choose a expansion timezone, we
  // eventually disregard the timezone if we try to apply it here.
  recurrenceDateRecurrences.forEach((rdate) => normalizedRuleSet.rdate(rdate));

  recurrenceRuleRecurrences.forEach((rule) => {
    const normalizedRuleOptions = {
      ...rule.origOptions,
      tzid: isDefined(tzid) ? tzid : rule.origOptions.tzid,
      dtstart: isDefined(dtstart) ? dtstart : rule.origOptions.dtstart,
    };

    normalizedRuleSet.rrule(new RRule(normalizedRuleOptions));
  });

  return normalizedRuleSet;
}
